import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { RoofEdge } from "building-types/roofs/roofEdgeTypes";
import type { RoofGenerationStageInput, RoofGenerationStageResult } from "building-types/roofs/roofGenerationTypes";
import type { RoofOpeningPlacement } from "building-types/roofs/roofSystemTypes";
import { RoofSide } from "building-types/settings/roofTypes";
import { RoofPeakDirection } from "building-types/settings/settingsTypesEnum";
import type { WallSettings } from "building-types/settings/wallSettings";
import { BuiltinWindowId } from "building-types/windows/windowId";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { faceNew, type Face } from "geom/brep";
import { planeNew, planeProjectPolyLine3 } from "geom/plane";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3New } from "geom/polyLine3";
import { vec2Dist } from "geom/vec2";
import { vec3Cross, vec3Normalize, vec3Sub, vec3AddScale } from "geom/vec3";
import { addEaveWallWindow, addGableWallWindow } from "../roofGableWindows";

function createEaveWallPlanes(
  eaveEdge: RoofEdge | undefined,
  ceilingHeight: number,
  wallSettings: WallSettings,
  exteriorWallOffsetWithFinish: number,
) {
  if (!eaveEdge) {
    return null;
  }

  // Create the eave rectangle.
  const polyline3: PolyLine3 = polyLine3New(15);
  polyline3[0] = eaveEdge.edge[0];
  polyline3[1] = eaveEdge.edge[1];
  polyline3[2] = ceilingHeight;
  polyline3[3] = eaveEdge.edge[0];
  polyline3[4] = eaveEdge.edge[1];
  polyline3[5] = eaveEdge.startHeight;
  polyline3[6] = eaveEdge.edge[2];
  polyline3[7] = eaveEdge.edge[3];
  polyline3[8] = eaveEdge.endHeight;
  polyline3[9] = eaveEdge.edge[2];
  polyline3[10] = eaveEdge.edge[3];
  polyline3[11] = ceilingHeight;
  polyline3[12] = polyline3[0];
  polyline3[13] = polyline3[1];
  polyline3[14] = polyline3[2];

  // Calculate U, V, and normal vectors.
  let [uX, uY, uZ] = vec3Sub(polyline3[6], polyline3[7], polyline3[8], polyline3[3], polyline3[4], polyline3[5]);
  [uX, uY, uZ] = vec3Normalize(uX, uY, uZ);
  const [vX, vY, vZ] = [0, 0, 1];
  const [nX, nY, nZ] = vec3Cross(uX, uY, uZ, vX, vY, vZ);

  // Calculate front and back planes.
  const [frontX, frontY, frontZ] = vec3AddScale(
    polyline3[0],
    polyline3[1],
    polyline3[2],
    nX,
    nY,
    nZ,
    exteriorWallOffsetWithFinish,
  );
  const frontPlane = planeNew(frontX, frontY, frontZ, uX, uY, uZ, vX, vY, vZ);

  const [backX, backY, backZ] = vec3AddScale(
    polyline3[9],
    polyline3[10],
    polyline3[11],
    nX,
    nY,
    nZ,
    -wallSettings.exteriorWallBaseThickness + exteriorWallOffsetWithFinish,
  );
  const backPlane = planeNew(backX, backY, backZ, -uX, -uY, -uZ, vX, vY, vZ);

  const polyline2 = planeProjectPolyLine3(frontPlane, polyline3);
  const frontFace = faceNew([polyline2], frontPlane);
  const span = vec2Dist(eaveEdge.edge[0], eaveEdge.edge[1], eaveEdge.edge[2], eaveEdge.edge[3]);

  return {
    frontFace,
    backPlane,
    span,
  };
}

/**
 * Inserts windows into the gable faces of the roof
 */
export async function generateRoofWindows({
  subRoofOptions,
  subRoof,
  temporaries,
}: RoofGenerationStageInput): Promise<RoofGenerationStageResult> {
  const { id: subRoofId, roofSystemId, roofVolume } = subRoof;

  const {
    baseHeight,
    leftSideSlopes,
    rightSideSlopes,
    span,
    leftSpan,
    rightSpan,
    peakHeight,
    verticalRails,
    rectangularOverhangs,
    rectangularExtWallOffsetWithFinish,
    startFaces,
    endFaces,
    perimeters,
    startConnectivity,
    endConnectivity,
    eaveEdges,
    ceilingHeight,
    startEdge,
  } = roofVolume;

  let { wallFaces } = subRoof;

  const { startBackFace, endBackFace } = temporaries;

  const { peakDirection } = perimeters;
  const { startWallFaces, endWallFaces } = wallFaces;
  const { face: startFrontFace } = startFaces;
  const { face: endFrontFace } = endFaces;

  const { baseLeftOverhang, baseRightOverhang } = rectangularOverhangs;

  const { isFree: isStartFree } = startConnectivity;
  const { isFree: isEndFree } = endConnectivity;

  const {
    roofInputSettings,
    windows,
    windowUnits,
    windowShutters,
    windowCatalog,
    roofOpenings = [],
    roofId,
    issues,
    mirror,
  } = subRoofOptions;

  const { wallSettings, propCatalog, assets, wainscotingSettings, getMergedSettingsForWindow } = roofInputSettings;

  const leftEaveEdge = eaveEdges.find((edge) => edge.side === RoofSide.LEFT);
  const rightEaveEdge = eaveEdges.find((edge) => edge.side === RoofSide.RIGHT);

  assertIsNotNullOrUndefined(rectangularExtWallOffsetWithFinish);
  const leftEdgeIndex = (startEdge + 1) % 4;
  const rightEdgeIndex = (startEdge + 3) % 4;
  const rightExteriorWallOffsetWithFinish = rectangularExtWallOffsetWithFinish[rightEdgeIndex];
  const leftExteriorWallOffsetWithFinish = rectangularExtWallOffsetWithFinish[leftEdgeIndex];

  const rightEaveWallPlanes = createEaveWallPlanes(
    rightEaveEdge,
    ceilingHeight,
    wallSettings,
    rightExteriorWallOffsetWithFinish,
  );
  const leftEaveWallPlanes = createEaveWallPlanes(
    leftEaveEdge,
    ceilingHeight,
    wallSettings,
    leftExteriorWallOffsetWithFinish,
  );

  const { isStartTruncated, truncatedStartHeight, isEndTruncated, truncatedEndHeight } = verticalRails;

  const startWindows: Table<GeneratedWindowPlacement> = {};
  const endWindows: Table<GeneratedWindowPlacement> = {};
  const leftEaveWindows: Table<GeneratedWindowPlacement> = {};
  const rightEaveWindows: Table<GeneratedWindowPlacement> = {};
  const hideInteriorFaces: Face[] = [];

  // helper function
  const addWindow = async (roofOpening: RoofOpeningPlacement, side: RoofSide): Promise<void> => {
    const maxStartHeight = isStartTruncated ? baseHeight + truncatedStartHeight : peakHeight;
    const maxEndHeight = isEndTruncated ? baseHeight + truncatedEndHeight : peakHeight;

    if (side === RoofSide.START || side === RoofSide.END) {
      let isEnd = side === RoofSide.END;

      if (mirror && peakDirection === RoofPeakDirection.BY_LOW_RAIL_INDEX) {
        isEnd = !isEnd;
      }

      const isFree = isEnd ? isEndFree : isStartFree;
      const frontFace = isEnd ? endFrontFace : startFrontFace;
      const backFace = (isEnd ? endBackFace : startBackFace) ?? null;
      if (!frontFace || !isFree) {
        return;
      }

      await addGableWallWindow({
        roofSystemId,
        subRoofId,
        baseHeight,
        roofOpening,
        span,
        leftSpan: isEnd ? rightSpan : leftSpan,
        rightSpan: isEnd ? leftSpan : rightSpan,
        leftSideSlopes: isEnd ? rightSideSlopes : leftSideSlopes,
        rightSideSlopes: isEnd ? leftSideSlopes : rightSideSlopes,
        leftWallToRoofEdgeDistance: isEnd ? baseRightOverhang : baseLeftOverhang,
        rightWallToRoofEdgeDistance: isEnd ? baseLeftOverhang : baseRightOverhang,
        maxHeight: isEnd ? maxEndHeight : maxStartHeight,
        frontFace,
        backFace,
        wallFaces: isEnd ? endWallFaces : startWallFaces,
        hideInteriorFaces,
        gableWindows: isEnd ? endWindows : startWindows,
        windows,
        windowUnits,
        windowShutters,
        wallSettings,
        propCatalog,
        assets,
        wainscotingSettings,
        issues,
        windowCatalog,
        mirror: !!mirror,
        getMergedSettingsForWindow,
        entityHandleForSelectingReportingUnit: { type: "ROOF", id: roofId } as DocEntityHandle,
        fallbackInstanceId: subRoof.componentInstanceChain?.[0],
      });
    } else {
      const rightEaveSide = mirror ? RoofSide.LEFT : RoofSide.RIGHT;
      const isRightSide = side === rightEaveSide;
      const eaveWallPlanes = isRightSide ? rightEaveWallPlanes : leftEaveWallPlanes;
      if (!eaveWallPlanes) {
        return;
      }
      const { frontFace, backPlane, span } = eaveWallPlanes;
      const maxHeight = (isRightSide ? rightEaveEdge?.startHeight : leftEaveEdge?.startHeight) ?? 0;

      await addEaveWallWindow({
        roofSystemId,
        subRoofId,
        baseHeight: ceilingHeight,
        roofOpening,
        span,
        maxHeight,
        frontFace,
        backPlane,
        hideInteriorFaces,
        windows,
        eaveWindows: isRightSide ? rightEaveWindows : leftEaveWindows,
        windowUnits,
        windowShutters,
        wallSettings,
        propCatalog,
        assets,
        wainscotingSettings,
        issues,
        windowCatalog,
        mirror: !!mirror,
        getMergedSettingsForWindow,
        entityHandleForSelectingReportingUnit: { type: "ROOF", id: roofId } as DocEntityHandle,
        fallbackInstanceId: subRoof.componentInstanceChain?.[0],
      });
    }
  };

  // insert windows
  for (const roofOpening of roofOpenings) {
    if (roofOpening.windowType === BuiltinWindowId.NO) {
      continue;
    }
    if (roofOpening.side) {
      await addWindow(roofOpening, roofOpening.side);
    } else {
      // Dormer windows can have no sides. Use start and end.
      await addWindow(roofOpening, RoofSide.START);
      await addWindow(roofOpening, RoofSide.END);
    }
  }

  // add the faces
  wallFaces = {
    ...wallFaces,
    hideInteriorFaces,
  };

  return {
    subRoof: {
      startWindows,
      endWindows,
      leftEaveWindows,
      rightEaveWindows,
      wallFaces,
    },
  };
}
